import { JSX } from 'react';
import { PageError, PageLoading } from '../../../components/page';
import { useSurveyQuestionnaireQuery } from '../../../graphQL';
import { RouteParams, useParams } from '../../../routes';
import { useTitle } from '../../../utils/useTitle';
import { SurveyPageWrapper } from './components/SurveyPageWrapper';
import { SurveyQuestionSlides } from './components/SurveyQuestionSlides';
import { useSurveyContext } from './contexts/surveyContext';
import { SurveySlideProvider } from './contexts/surveySlideContext';
import { useSurveyRedirect } from './hooks/useSurveyRedirect';
import { validateQuestionnaireRoute } from './utils/validators';

type SurveySlidesPageParams = RouteParams<'surveyAssessments'>;

export const SurveyQuestionsPage = (): JSX.Element => {
  useTitle(['Student Survey']);
  useSurveyRedirect();

  const params = useParams<SurveySlidesPageParams>();
  const { questionnaireMapping, loading: contextLoading } = useSurveyContext();

  const { questionnaireRoute: rawQuestionnaireRoute } = params;

  const questionnaireRoute = validateQuestionnaireRoute(
    typeof rawQuestionnaireRoute === 'string' ? rawQuestionnaireRoute : undefined,
  );

  const questionnaireKey =
    questionnaireRoute !== null ? questionnaireMapping[questionnaireRoute] : '';

  const {
    data: questionnaireData,
    loading: questionnaireLoading,
    error,
  } = useSurveyQuestionnaireQuery({
    variables: { questionnaireKey },
    skip: questionnaireKey === '', // Skip the query if we don't have a valid key
  });

  if (contextLoading || questionnaireLoading) {
    return <PageLoading pageName="Student Survey" />;
  }

  if (error || questionnaireRoute === null || !questionnaireData?.surveyQuestionnaire) {
    return <PageError />;
  }

  return (
    <SurveySlideProvider key={questionnaireRoute}>
      <SurveyPageWrapper title="Student Survey" testID="sai-survey-slides">
        <SurveyQuestionSlides
          questionnaireRoute={questionnaireRoute}
          questionnaire={questionnaireData.surveyQuestionnaire}
        />
      </SurveyPageWrapper>
    </SurveySlideProvider>
  );
};
